import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CalendarPlus, X, Undo2, CheckCircle2, Receipt, Clock, DollarSign } from "lucide-react";
import { fmtDate, fmtDateTime } from "@/lib/datetime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WAIVER_PER_DAY = 15;

const money = (v) => `$${(parseFloat(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const daysBetween = (a, b) => {
  if (!a || !b) return 0;
  const d = Math.round((new Date(b + "T00:00:00") - new Date(a + "T00:00:00")) / 86400000);
  return d;
};

export default function ExtendRental({ booking, token, onSaved }) {
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  // Wizard fields
  const [newReturn, setNewReturn] = useState("");
  const [useWeekly, setUseWeekly] = useState(false);
  const [waiver, setWaiver] = useState(false);
  const [coverage, setCoverage] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [discountType, setDiscountType] = useState("none");
  const [discountValue, setDiscountValue] = useState("");
  const [paymentMode, setPaymentMode] = useState("later");
  const [amountPaid, setAmountPaid] = useState("");
  const [payMethod, setPayMethod] = useState("Card");
  const [note, setNote] = useState("");
  const [reqKey, setReqKey] = useState(() => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`));
  const [expanded, setExpanded] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/bookings/${booking.id}/rental-summary`, auth);
      setSummary(data);
      setWaiver(!!data.booking.damage_waiver); // auto-on if original rental had waiver
      setCoverage(!!data.booking.coverage_selected); // protections continue unless management changes them
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load rental summary.");
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking.id]);

  useEffect(() => { if (open) loadSummary(); }, [open, loadSummary]);

  const b = summary?.booking;
  const curReturn = b?.return_date || (booking.return_date || "").slice(0, 10);
  const daily = b?.daily_rate ?? (parseFloat(booking.daily_rate) || 0);
  const weekly = b?.weekly_rate ?? 0;

  // Live pricing preview (mirrors backend create_extension)
  const days = daysBetween(curReturn, newReturn);
  const dailyOption = +(daily * Math.max(days, 0)).toFixed(2);
  const weeklyOption = weekly > 0 ? +(Math.ceil(Math.max(days, 0) / 7) * weekly).toFixed(2) : 0;
  const weeklyCheaper = weekly > 0 && weeklyOption < dailyOption;
  const baseCharge = useWeekly && weekly > 0 && weeklyOption < dailyOption ? weeklyOption : dailyOption;
  let discountAmount = 0;
  if (discountType === "dollar") discountAmount = Math.min(parseFloat(discountValue) || 0, baseCharge);
  else if (discountType === "percent") discountAmount = +(baseCharge * (parseFloat(discountValue) || 0) / 100).toFixed(2);
  const waiverPerDay = b?.waiver_per_day ?? WAIVER_PER_DAY;
  const covPerDay = b?.coverage_per_day ?? 0;
  const youngPerDay = b?.young_per_day ?? 0;
  const extraPerDay = b?.extra_driver_per_day ?? 0;
  const waiverAmount = waiver ? +(waiverPerDay * Math.max(days, 0)).toFixed(2) : 0;
  const coverageAmount = coverage ? +(covPerDay * Math.max(days, 0)).toFixed(2) : 0;
  const youngAmount = +(youngPerDay * Math.max(days, 0)).toFixed(2);
  const extraAmount = +(extraPerDay * Math.max(days, 0)).toFixed(2);
  const total = +(baseCharge - discountAmount + waiverAmount + coverageAmount + youngAmount + extraAmount).toFixed(2);
  const paid = paymentMode === "later" ? 0 : paymentMode === "full" ? total : Math.min(parseFloat(amountPaid) || 0, total);
  const balance = +(total - paid).toFixed(2);

  const resetForm = () => {
    setNewReturn(""); setUseWeekly(false); setDiscountType("none"); setDiscountValue("");
    setPaymentMode("later"); setAmountPaid(""); setNote(""); setOverrideReason(""); setConfirming(false);
    setReqKey(crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
  };

  const submit = async (force = false, scheduleOverrideReason = "") => {
    if (!newReturn) { toast.error("Choose the new return date."); return; }
    if (days <= 0) { toast.error("New return date must be after the current return date."); return; }
    if (coverage !== !!b?.coverage_selected && !overrideReason.trim()) {
      toast.error("Insurance differs from the renter's original choice — enter the override reason first.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/bookings/${booking.id}/extensions`, {
        new_return_date: newReturn,
        daily_rate: daily,
        weekly_rate: weekly,
        use_weekly: useWeekly,
        coverage_enabled: coverage,
        override_reason: overrideReason.trim(),
        discount_type: discountType,
        discount_value: parseFloat(discountValue) || 0,
        waiver_enabled: waiver,
        amount_paid: paid,
        payment_mode: paymentMode,
        payment_method: paymentMode === "later" ? "later" : payMethod,
        note,
        request_key: reqKey,
        force,
        schedule_override_reason: scheduleOverrideReason,
      }, auth);
      if (data.duplicate) {
        toast.info(`Extension #${data.extension.number} was already recorded — no duplicate created.`);
      } else if (data.payment_post_error) {
        // The rental extension itself went through, but the payment did NOT get credited —
        // never let this look like a normal success. No retry/recharge suggested here.
        toast.error(
          `Extension #${data.extension.number} saved, but the PAYMENT FAILED TO RECORD: ${data.payment_post_error}. `
          + `True balance is ${money(data.extension.balance)}, not $0. Do NOT re-charge the customer — check whether the `
          + `money was actually collected, then record it ONCE via this booking's Financials screen.`,
          { duration: 20000 });
      } else if (data.post_processing_error) {
        toast.warning(
          `Extension #${data.extension.number} created and payment recorded (${money(data.extension.amount_paid)}) — `
          + `but the invoice/receipt document failed to generate: ${data.post_processing_error}. Money is correctly `
          + `recorded; only the paperwork is missing. No action needed on payment — documents can be reissued later if needed.`,
          { duration: 15000 });
      } else {
        toast.success(`Extension #${data.extension.number} created — +${data.extension.days}d → returns ${data.extension.new_return_date}.`
          + (data.extension.points_earned ? ` +${data.extension.points_earned.toLocaleString()} loyalty pts.` : "")
          + ` ${data.agreement_auto_signed ? "Extension agreement auto-signed & emailed." : data.agreement_issued ? "New agreement sent." : ""} Customer confirmation sent.`);
      }
      resetForm();
      await loadSummary();
      onSaved?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (e?.response?.status === 409 && !force) {
        setBusy(false);
        const isScheduleConflict = /handoff|that time|return time/i.test(detail);
        if (isScheduleConflict) {
          const reason = window.prompt(
            `⚠ ${detail}\n\nThis looks like a genuine handoff-slot scheduling conflict, not a vehicle-availability issue. `
            + `Enter a management override reason to book it anyway (audited), or leave blank to cancel:`, "");
          if (reason && reason.trim()) return await submit(true, reason.trim());
          return;
        }
        if (window.confirm(`⚠ ${detail || "This extension overlaps another reservation."}`)) {
          return await submit(true);
        }
        return;
      }
      toast.error(detail || "Could not create the extension.");
    } finally { setBusy(false); }
  };

  const shareLink = async (sms) => {
    try {
      const { data } = await axios.post(`${API}/bookings/${booking.id}/extend-link?send_sms=${sms}`, {}, auth);
      if (sms) toast.success("Extension link texted to the customer.");
      else { await navigator.clipboard.writeText(data.url); toast.success("Extension link copied to clipboard."); }
    } catch { toast.error("Could not create the extension link."); }
  };

  const resetReissue = async (ex) => {
    if (!window.confirm("This will cancel the current approved extension and restore the booking to its previous return date. The renter will need to submit and pay for a new extension. Continue?")) return;
    const reason = window.prompt("Optional note / reason for the reset (kept in the audit trail):",
      "Customer requested shorter extension / unable to afford original extension.");
    if (reason === null) return;
    const sendSms = !!b?.phone && window.confirm("Text the renter their fresh extension link now?");
    const sendEmail = !!b?.email && window.confirm("Also email the renter the fresh extension link?");
    try {
      const { data } = await axios.post(`${API}/bookings/${booking.id}/extensions/${ex.id}/reset`,
        { reason, send_sms: sendSms, send_email: sendEmail }, auth);
      toast.success(`Extension #${ex.number} cancelled — booking restored to ${data.return_date}.${data.sent_sms ? " New link texted." : ""}${data.sent_email ? " New link emailed." : ""}`);
      if (data.payment_collected) toast.warning(data.payment_note, { duration: 10000 });
      if (!data.sent_sms && !data.sent_email) {
        try { await navigator.clipboard.writeText(data.extend_url); toast.info("Fresh extension link copied to clipboard."); } catch {}
      }
      await loadSummary();
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reset the extension."); }
  };

  const exts = summary?.extensions || [];
  const cancelledExts = summary?.cancelled_extensions || [];
  const fin = summary?.financials;
  const ledger = summary?.ledger || [];
  const extCount = exts.length;
  const loyalty = summary?.loyalty;
  const audit = summary?.audit || [];
  const extRevenue = +(exts.reduce((s, e) => s + (parseFloat(e.total) || 0), 0)).toFixed(2);
  const extDays = exts.reduce((s, e) => s + (parseInt(e.days) || 0), 0);
  const to12 = (t) => { if (!t) return ""; if (/am|pm/i.test(String(t))) return String(t); const [h, m] = String(t).split(":"); const H = parseInt(h) || 0; return `${((H + 11) % 12) + 1}:${m || "00"} ${H >= 12 ? "PM" : "AM"}`; };

  const dailyBreakdown = (ex) => {
    const out = [];
    if (!ex.prev_return_date || !ex.new_return_date || ex.pricing_mode === "weekly") return out;
    const rate = parseFloat(ex.daily_rate) || 0;
    const d = new Date(ex.prev_return_date + "T00:00:00");
    if (isNaN(d.getTime())) return out;
    for (let i = 0; i < Math.min(ex.days || 0, 31); i++) {
      d.setDate(d.getDate() + 1);
      const lbl = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
      out.push({ date: lbl, rate, pts: Math.round(rate * 2), through: lbl });
    }
    return out;
  };

  return (
    <>
      <button data-testid={`extend-rental-btn-${booking.id}`} onClick={() => setOpen((o) => !o)}
        className="text-xs flex items-center gap-1.5 border border-[#F2B705]/40 text-[#F2B705] px-3 py-1.5 rounded hover:bg-[#F2B705] hover:text-black transition-colors">
        <CalendarPlus size={13} /> Extend Rental{extCount ? ` (${extCount})` : ""}
      </button>

      {open && (
        <div data-testid={`extend-rental-panel-${booking.id}`} className="w-full mt-2 rounded-lg border border-[#F2B705]/30 bg-[#0A0A0A] p-4 space-y-5 max-h-[80vh] overflow-y-auto">
          <div className="flex items-center justify-between sticky top-0 bg-[#0A0A0A] pb-2 z-10">
            <span className="text-sm font-semibold text-[#F2B705] flex items-center gap-1.5"><CalendarPlus size={15} /> Rental Extensions</span>
            <button data-testid={`extend-close-${booking.id}`} onClick={() => setOpen(false)} className="text-white/40 hover:text-white"><X size={16} /></button>
          </div>

          {loading && <div className="flex items-center gap-2 text-white/50 text-sm"><Loader2 className="animate-spin" size={14} /> Loading rental summary…</div>}

          {/* Extension Summary Dashboard */}
          {fin && (
            <div data-testid={`extension-summary-${booking.id}`} className="rounded-lg border border-[#F2B705]/25 bg-[#0C0C0C] p-3">
              <div className="text-xs uppercase tracking-widest text-[#F2B705] mb-2">Extension Summary</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Stat label="Original Rental" value={money(fin.original_rental)} />
                <Stat label="Extension Revenue" value={money(extRevenue)} tone="gold" />
                <Stat label="Extensions" value={`${extCount}`} />
                <Stat label="Extension Days" value={`${extDays}`} />
                <Stat label="Current Return" value={`${curReturn ? fmtDate(curReturn) : "—"}${b?.return_time ? ` ${to12(b.return_time)}` : ""}`} />
                <Stat label="Total Rental Revenue" value={money((fin.original_rental || 0) + extRevenue)} />
                <Stat label="Amount Paid" value={money(fin.payments_received)} tone="emerald" />
                <Stat label="Remaining Balance" value={money(fin.remaining_balance)} tone={fin.remaining_balance > 0.01 ? "gold" : "emerald"} />
              </div>
              {loyalty && (
                <div data-testid={`extension-loyalty-${booking.id}`} className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 pt-2 border-t border-white/10">
                  <Stat label="Points This Rental" value={(loyalty.points_this_rental || 0).toLocaleString()} tone="gold" />
                  <Stat label="Total Loyalty Points" value={(loyalty.balance || 0).toLocaleString()} tone="gold" />
                  <Stat label="Club Status" value={loyalty.tier || "Member"} tone={loyalty.tier !== "Member" ? "gold" : undefined} />
                  <Stat label="Next Reward" value={loyalty.next_reward ? `${loyalty.next_reward.title} — ${loyalty.next_reward.needed.toLocaleString()} pts away` : "All rewards unlocked"} />
                </div>
              )}
            </div>
          )}

          {/* Financial Dashboard */}
          {fin && (
            <div data-testid={`extend-financials-${booking.id}`} className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Stat label="Original Rental" value={money(fin.original_rental)} />
              <Stat label="Extensions" value={money(fin.extensions)} />
              <Stat label="Damage Waivers" value={money(fin.damage_waivers)} />
              <Stat label="Discounts" value={`-${money(fin.discounts)}`} />
              <Stat label="Payments Received" value={money(fin.payments_received)} tone="emerald" />
              <Stat label="Remaining Balance" value={money(fin.remaining_balance)} tone={fin.remaining_balance > 0.01 ? "gold" : "emerald"} />
              <Stat label="Taxes" value={money(0)} />
              <Stat label="Current Return" value={curReturn ? fmtDate(curReturn) : "—"} />
            </div>
          )}

          {/* Extension Timeline */}
          {extCount > 0 && (
            <div data-testid={`extension-timeline-${booking.id}`} className="border-t border-white/10 pt-3">
              <div className="text-xs uppercase tracking-widest text-white/40 mb-2 flex items-center gap-1.5"><Clock size={12} /> Extension Timeline</div>
              <div className="space-y-2">
                {exts.map((ex, i) => (
                  <div key={ex.id} data-testid={`extension-timeline-row-${ex.id}`} className="bg-[#0C0C0C] border border-white/10 rounded px-3 py-2.5">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 h-6 w-6 shrink-0 rounded-full bg-[#F2B705]/15 text-[#F2B705] text-xs font-bold flex items-center justify-center">#{ex.number}</div>
                      <button data-testid={`extension-expand-${ex.id}`} onClick={() => setExpanded(expanded === ex.id ? null : ex.id)} className="flex-1 text-left text-sm cursor-pointer">
                        <div className="text-white/85">
                          <span className="text-[#F2B705] font-semibold">+{ex.days} day(s)</span> · {ex.prev_return_date} → {ex.new_return_date}
                          <span className="ml-2 text-[10px] uppercase tracking-wide text-white/40 border border-white/15 rounded px-1.5 py-0.5">{ex.pricing_mode}</span>
                          <span className="ml-2 text-[10px] text-white/30">{expanded === ex.id ? "▲ less" : "▼ details"}</span>
                        </div>
                        <div className="text-white/45 text-xs mt-1 flex flex-wrap gap-x-3">
                          <span>Charge {money(ex.base_charge)}</span>
                          {ex.waiver_amount > 0 && <span>Waiver {money(ex.waiver_amount)}</span>}
                          {ex.discount_amount > 0 && <span>Disc -{money(ex.discount_amount)}</span>}
                          <span>Total {money(ex.total)}</span>
                          <span className="text-emerald-400/80">Paid {money(ex.amount_paid)}</span>
                          <span className={ex.balance > 0.01 ? "text-[#F2B705]" : "text-emerald-400/80"}>Bal {money(ex.balance)}</span>
                        </div>
                        <div className="text-white/30 text-[11px] mt-0.5">{fmtDate(ex.created_at)}</div>
                      </button>
                      {i === exts.length - 1 && (
                        <button data-testid={`extension-reset-${ex.id}`} onClick={() => resetReissue(ex)}
                          className="shrink-0 text-xs flex items-center gap-1 border border-[#F2B705]/50 text-[#F2B705] px-2 py-1 rounded hover:bg-[#F2B705] hover:text-black transition-colors"
                          title="Safely cancel this extension — the full record is preserved in the audit history for any payment dispute.">
                          <Undo2 size={12} /> Reset / Reissue Extension
                        </button>
                      )}
                    </div>
                    {expanded === ex.id && (
                      <div data-testid={`extension-detail-${ex.id}`} className="mt-2.5 ml-9 border-t border-white/10 pt-2.5 space-y-2">
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5 text-[11px]">
                          {[["Created", `${fmtDateTime(ex.created_at)}`],
                            ["Daily rate", money(ex.daily_rate)], ["Weekly rate", ex.weekly_rate ? money(ex.weekly_rate) : "—"],
                            ["Subtotal", money(ex.base_charge)], ["Discount", ex.discount_amount ? `-${money(ex.discount_amount)}` : "—"],
                            ["Damage waiver", ex.waiver_amount ? money(ex.waiver_amount) : "—"],
                            ["Coverage", ex.coverage_amount ? money(ex.coverage_amount) : "—"], ["Taxes", money(ex.tax || 0)],
                            ["Insurance", ex.coverage_enabled ? `All-In ($${ex.coverage_per_day || 0}/day)` : "Own insurance"],
                            ...(ex.insurance_overridden ? [["Ins. override", `⚠️ ${ex.override_reason || "yes"}`]] : []),
                            ["Final total", money(ex.total)], ["Amount paid", money(ex.amount_paid)], ["Balance", money(ex.balance)],
                            ["Payment method", ex.payment_method || "record"], ["Receipt #", ex.receipt_number || "—"],
                            ["Invoice #", ex.invoice_number || "—"], ["Agreement", ex.agreement_number ? `${ex.agreement_number} ✓` : "not issued"],
                            ["Processed by", ex.created_by || "admin"],
                            ["Points earned", (ex.points_earned ?? Math.round((ex.amount_paid || 0) * 2)).toLocaleString()],
                            ["SMS confirm", ex.confirm_sms_status || "—"], ["Email confirm", ex.confirm_email_status || "—"],
                          ].map(([l, v]) => (
                            <div key={l}><span className="text-white/35">{l}: </span><span className="text-white/75">{v}</span></div>
                          ))}
                        </div>
                        {ex.note && <div className="text-[11px] text-white/55 bg-black/40 border border-white/10 rounded px-2 py-1.5">📝 {ex.note}</div>}
                        {ex.pricing_mode !== "weekly" && ex.days > 0 && (
                          <div data-testid={`extension-daily-breakdown-${ex.id}`}>
                            <div className="text-[10px] uppercase tracking-wide text-white/35 mb-1">Daily breakdown (visual only)</div>
                            <div className="space-y-0.5">
                              {dailyBreakdown(ex).map((d, j) => (
                                <div key={j} className="flex items-center justify-between text-[11px] bg-black/30 border border-white/5 rounded px-2 py-1">
                                  <span className="text-white/60">{d.date}</span>
                                  <span className="text-white/70">{money(d.rate)}</span>
                                  <span className="text-[#F2B705]/70">{d.pts} pts</span>
                                  <span className="text-white/40">Paid through {d.through}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Cancelled / reset extensions — permanent audit history */}
          {cancelledExts.length > 0 && (
            <div data-testid={`cancelled-extensions-${booking.id}`} className="border-t border-white/10 pt-3">
              <div className="text-xs uppercase tracking-widest text-red-300/70 mb-2">Cancelled Extensions (audit history)</div>
              <div className="space-y-1.5">
                {cancelledExts.map((cx) => (
                  <div key={cx.id} data-testid={`cancelled-extension-row-${cx.id}`} className="bg-[#0C0C0C] border border-red-400/20 rounded px-3 py-2 text-xs">
                    <div className="text-white/70">
                      <span className="text-red-300 font-bold uppercase text-[10px] border border-red-400/40 rounded px-1.5 py-0.5 mr-2">Cancelled by Admin</span>
                      Ext #{cx.number} · +{cx.days} day(s) · {cx.prev_return_date} → {cx.new_return_date} · Total {money(cx.total)}
                      {(cx.cash_collected ?? cx.amount_paid) > 0 && <span className="text-emerald-400/80 ml-2">Paid {money(cx.cash_collected ?? cx.amount_paid)} (kept as credit)</span>}
                    </div>
                    <div className="text-white/40 mt-1">
                      Approved {fmtDate(cx.created_at)} by {cx.created_by || "admin"} · Reset {fmtDate(cx.cancelled_at)} by {cx.cancelled_by}
                      {cx.cancel_reason && <span className="italic"> — "{cx.cancel_reason}"</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* New Extension — single scrolling wizard */}
          <div data-testid={`extension-wizard-${booking.id}`} className="border-t border-white/10 pt-4 space-y-4">
            <div className="text-xs uppercase tracking-widest text-white/40">Add an Extension</div>

            {/* 1. Quick Extensions + summary card */}
            <Section n={1} title="Quick Extensions">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {[[1, "+1 Day"], [3, "+3 Days"], [7, "+7 Days (Weekly)"]].map(([n, l]) => (
                  <button key={n} data-testid={`ext-quick-${n}-${booking.id}`}
                    onClick={() => {
                      if (!curReturn) return;
                      const d = new Date(curReturn + "T00:00:00");
                      d.setDate(d.getDate() + n);
                      setNewReturn(d.toISOString().slice(0, 10));
                      setUseWeekly(n === 7 && weekly > 0);
                    }}
                    className={`text-sm font-bold px-4 py-2.5 rounded-lg border transition-colors ${days === n ? "border-[#F2B705] bg-[#F2B705] text-black" : "border-white/15 text-white/70 hover:border-[#F2B705]/50 hover:text-[#F2B705]"}`}>
                    {l}
                  </button>
                ))}
                <span className="text-white/40 text-xs ml-1">or custom date:</span>
                <input type="date" data-testid={`ext-new-return-${booking.id}`} value={newReturn} min={curReturn}
                  onChange={(e) => setNewReturn(e.target.value)}
                  className="bg-[#0C0C0C] border border-white/10 rounded px-3 py-2 text-sm text-white [color-scheme:dark] focus:outline-none focus:border-[#F2B705]/50" />
              </div>
              <div data-testid={`ext-summary-card-${booking.id}`} className="grid grid-cols-1 sm:grid-cols-3 gap-2 rounded-lg border border-white/10 bg-[#0C0C0C] p-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40">Current Return Date</div>
                  <div className="text-sm font-semibold text-white/85">{curReturn ? fmtDate(curReturn) : "—"}{b?.return_time ? ` @ ${to12(b.return_time)}` : ""}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40">New Return Date</div>
                  <div className={`text-sm font-semibold ${newReturn ? "text-[#F2B705]" : "text-white/30"}`}>{newReturn ? `${fmtDate(newReturn)}${b?.return_time ? ` @ ${to12(b.return_time)}` : ""}` : "choose above"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40">Extension</div>
                  <div className={`text-sm font-bold ${days > 0 ? "text-emerald-400" : "text-white/30"}`}>{days > 0 ? `+${days} Day${days !== 1 ? "s" : ""}` : "—"}</div>
                </div>
              </div>
            </Section>

            {/* 2. Pricing */}
            <Section n={2} title="Pricing">
              <div className="space-y-2">
                <label className={`flex items-center justify-between gap-2 rounded border px-3 py-2 cursor-pointer ${!useWeekly ? "border-[#F2B705]/60 bg-[#F2B705]/5" : "border-white/10"}`}>
                  <span className="flex items-center gap-2 text-sm text-white/80">
                    <input type="radio" data-testid={`ext-pricing-daily-${booking.id}`} checked={!useWeekly} onChange={() => setUseWeekly(false)} className="accent-[#F2B705]" />
                    Base Rental · {money(daily)}/day × {Math.max(days, 0)} <span className="text-white/35 text-xs">(coverage & fees itemized separately)</span>
                  </span>
                  <span className="text-white font-semibold">{money(dailyOption)}</span>
                </label>
                {weekly > 0 && (
                  <label className={`flex items-center justify-between gap-2 rounded border px-3 py-2 cursor-pointer ${useWeekly ? "border-[#F2B705]/60 bg-[#F2B705]/5" : "border-white/10"}`}>
                    <span className="flex items-center gap-2 text-sm text-white/80">
                      <input type="radio" data-testid={`ext-pricing-weekly-${booking.id}`} checked={useWeekly} onChange={() => setUseWeekly(true)} className="accent-[#F2B705]" />
                      Weekly Block · {money(weekly)}/wk × {Math.ceil(Math.max(days, 0) / 7)}
                      <span className="text-[10px] text-[#F2B705]/80 border border-[#F2B705]/40 rounded px-1.5 py-0.5">mgmt approval</span>
                      {weeklyCheaper && <span className="text-[10px] text-emerald-400 border border-emerald-400/40 rounded px-1.5 py-0.5">cheaper</span>}
                    </span>
                    <span className="text-white font-semibold">{money(weeklyOption)}</span>
                  </label>
                )}
                {weekly <= 0 && <p className="text-white/35 text-xs">No weekly rate configured — daily pricing applies.</p>}
              </div>
            </Section>

            {/* 3. Protections continue for every extension day */}
            <Section n={3} title="Protection Products">
              <div className="mb-2 flex items-center gap-2 text-xs" data-testid={`ext-insurance-origin-${booking.id}`}>
                <span className="text-white/45">Renter's original insurance:</span>
                <span className={`font-bold px-2 py-0.5 rounded-full border text-[11px] ${b?.coverage_selected ? "border-[#F2B705]/50 text-[#F2B705] bg-[#F2B705]/10" : "border-white/20 text-white/70"}`}>
                  {b?.coverage_selected ? `All-In Coverage — $${covPerDay}/day` : "Own insurance (no All-In charge)"}
                </span>
                <span className="text-white/30">carries forward automatically</span>
              </div>
              {covPerDay > 0 && (
                <label className="flex items-center gap-2 cursor-pointer mb-1">
                  <input type="checkbox" data-testid={`ext-coverage-${booking.id}`} checked={coverage} onChange={(e) => setCoverage(e.target.checked)} className="h-4 w-4 accent-[#F2B705]" />
                  <span className="text-sm text-white/80">All-In Coverage — ${covPerDay}/day × {Math.max(days, 0)} = <span className="text-white font-semibold">{money(coverageAmount)}</span></span>
                </label>
              )}
              {coverage !== !!b?.coverage_selected && (
                <div className="mb-2 rounded border border-amber-400/40 bg-amber-400/5 p-2.5" data-testid={`ext-override-box-${booking.id}`}>
                  <p className="text-amber-300 text-xs font-semibold">⚠️ Manager override — this differs from the renter's original insurance choice. A reason is required and logged in the audit trail.</p>
                  <input data-testid={`ext-override-reason-${booking.id}`} value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} maxLength={300}
                    placeholder="Override reason (required)"
                    className="mt-1.5 w-full bg-black border border-amber-400/30 rounded px-2.5 py-1.5 text-xs text-white placeholder:text-white/30 focus:outline-none focus:border-amber-400/70" />
                </div>
              )}
              <label data-testid={`ext-waiver-card-${booking.id}`} className={`flex items-center gap-3 cursor-pointer rounded-lg border-2 p-3 transition-colors ${waiver ? "border-[#F2B705]/60 bg-[#F2B705]/5" : "border-white/10 hover:border-white/25"}`}>
                <input type="checkbox" data-testid={`ext-waiver-${booking.id}`} checked={waiver} onChange={(e) => setWaiver(e.target.checked)} className="h-4 w-4 accent-[#F2B705]" />
                <span className="text-sm text-white/85"><b>Damage Waiver</b> — <span className="text-[#F2B705] font-bold">${waiverPerDay}/day</span> × {Math.max(days, 0)} = <span className="text-white font-semibold">{money(waiverAmount)}</span></span>
              </label>
              {b?.damage_waiver && <p className="text-white/35 text-xs mt-1">Auto-enabled — the original rental had the Damage Waiver.</p>}
              {(youngAmount > 0 || extraAmount > 0) && (
                <p className="text-white/45 text-xs mt-2">
                  Driver fees continue automatically:
                  {youngAmount > 0 && <> Young Driver ${youngPerDay}/day = {money(youngAmount)}.</>}
                  {extraAmount > 0 && <> Additional Driver ${extraPerDay}/day = {money(extraAmount)}.</>}
                </p>
              )}
            </Section>

            {/* 4. Discount */}
            <Section n={4} title="Discount (optional)">
              <div className="flex items-center gap-2 flex-wrap">
                <select data-testid={`ext-discount-type-${booking.id}`} value={discountType} onChange={(e) => setDiscountType(e.target.value)}
                  className="bg-[#0C0C0C] border border-white/10 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-[#F2B705]/50">
                  <option value="none">No discount</option>
                  <option value="dollar">$ off</option>
                  <option value="percent">% off</option>
                </select>
                {discountType !== "none" && (
                  <input type="number" min="0" step="0.01" data-testid={`ext-discount-value-${booking.id}`} value={discountValue} onChange={(e) => setDiscountValue(e.target.value)}
                    placeholder={discountType === "percent" ? "%" : "$"} className="w-24 bg-[#0C0C0C] border border-white/10 rounded px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#F2B705]/50" />
                )}
                {discountAmount > 0 && <span className="text-white/60 text-sm">= -{money(discountAmount)}</span>}
              </div>
            </Section>

            {/* 5. Payment (record only) */}
            <Section n={5} title="After Approval — how will you collect?">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                {[["later", "Send payment link after approval"], ["full", "Record full payment now"], ["partial", "Record partial payment"]].map(([v, l]) => (
                  <button key={v} data-testid={`ext-pay-${v}-${booking.id}`} onClick={() => setPaymentMode(v)}
                    className={`text-xs px-3 py-1.5 rounded border transition-colors ${paymentMode === v ? "border-[#F2B705] bg-[#F2B705] text-black font-semibold" : "border-white/15 text-white/60 hover:bg-white/10"}`}>{l}</button>
                ))}
              </div>
              {paymentMode !== "later" && (
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className="text-white/45 text-xs">Method:</span>
                  {["Cash", "Square", "Cash App", "Zelle", "Apple Pay", "Card", "Other"].map((m) => (
                    <button key={m} data-testid={`ext-method-${m.replace(" ", "-")}-${booking.id}`} onClick={() => setPayMethod(m)}
                      className={`text-xs px-2.5 py-1 rounded border transition-colors ${payMethod === m ? "border-[#F2B705] bg-[#F2B705]/15 text-[#F2B705] font-semibold" : "border-white/15 text-white/50 hover:bg-white/10"}`}>{m}</button>
                  ))}
                </div>
              )}
              {paymentMode === "partial" && (
                <input type="number" min="0" step="0.01" data-testid={`ext-amount-paid-${booking.id}`} value={amountPaid} onChange={(e) => setAmountPaid(e.target.value)}
                  placeholder="Amount collected" className="w-40 bg-[#0C0C0C] border border-white/10 rounded px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#F2B705]/50" />
              )}
              <textarea data-testid={`ext-note-${booking.id}`} value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={500}
                placeholder="Internal note (optional — staff only)"
                className="mt-2 w-full bg-[#0C0C0C] border border-white/10 rounded px-3 py-2 text-xs text-white placeholder:text-white/30 focus:outline-none focus:border-[#F2B705]/50" />
              <p className="text-white/35 text-xs mt-1">Logs the amount for record-keeping. No card is charged here.</p>
            </Section>

            {/* 6. Review + submit */}
            <div data-testid={`ext-review-${booking.id}`} className="rounded-lg border border-[#F2B705]/30 bg-[#0C0C0C] p-3 space-y-1.5 text-sm">
              <div className="flex items-center gap-1.5 text-[#F2B705] text-xs uppercase tracking-widest mb-1"><Receipt size={12} /> Extension Invoice</div>
              <Row label="Previous paid-through" value={curReturn ? fmtDate(curReturn) : "—"} />
              <Row label="New paid-through / return" value={newReturn ? fmtDate(newReturn) : "—"} bold />
              <Row label="Days added" value={`+${Math.max(days, 0)} day(s)`} />
              <div className="border-t border-white/10 my-1" />
              <Row label={useWeekly && weeklyCheaper ? "Base Rental — Weekly Block" : `Base Rental — ${Math.max(days, 0)} day(s) × ${money(daily)}`} value={money(baseCharge)} />
              {coverageAmount > 0 && <Row label={`All-In Coverage — ${Math.max(days, 0)} day(s) × ${money(covPerDay)}`} value={money(coverageAmount)} />}
              {waiverAmount > 0 && <Row label={`Damage Waiver — ${Math.max(days, 0)} day(s) × ${money(waiverPerDay)}`} value={money(waiverAmount)} />}
              {youngAmount > 0 && <Row label="Young Driver Fee" value={money(youngAmount)} />}
              {extraAmount > 0 && <Row label="Additional Driver Fee" value={money(extraAmount)} />}
              <Row label="Taxes" value={money(0)} />
              {discountAmount > 0 && <Row label="Discount" value={`-${money(discountAmount)}`} tone="emerald" />}
              <div className="border-t border-white/10 my-1" />
              <Row label="Total Due" value={money(total)} bold tone="gold" />
              <Row label="Recorded Payment" value={money(paid)} tone="emerald" />
              <Row label="Extension Balance" value={money(balance)} tone={balance > 0.01 ? "gold" : "emerald"} bold />
            </div>

            {days > 0 && (
              <div data-testid={`ext-mgmt-summary-${booking.id}`} className="rounded-lg border border-emerald-500/25 bg-emerald-500/[0.04] p-3 space-y-1 text-sm">
                <div className="text-emerald-300 text-[11px] uppercase tracking-widest mb-1">Management Summary</div>
                <Row label="Rental revenue" value={money(baseCharge)} />
                {coverageAmount > 0 && <Row label="Coverage revenue" value={money(coverageAmount)} />}
                {waiverAmount > 0 && <Row label="Waiver revenue" value={money(waiverAmount)} />}
                {(youngAmount + extraAmount) > 0 && <Row label="Driver fee revenue" value={money(youngAmount + extraAmount)} />}
                {discountAmount > 0 && <Row label="Less discount" value={`-${money(discountAmount)}`} tone="emerald" />}
                <Row label="Total extension revenue" value={money(total)} bold tone="gold" />
              </div>
            )}

            {!confirming ? (
              <button data-testid={`ext-create-${booking.id}`} disabled={busy || days <= 0}
                onClick={() => {
                  if (!newReturn) { toast.error("Choose the new return date."); return; }
                  if (days <= 0) { toast.error("New return date must be after the current paid-through date — the same day is never counted twice."); return; }
                  setConfirming(true);
                }}
                className="w-full bg-[#F2B705] text-black font-semibold text-sm px-4 py-2.5 rounded hover:bg-white transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                <CheckCircle2 size={15} /> Review & Approve Extension
              </button>
            ) : (
              <div data-testid={`ext-confirm-panel-${booking.id}`} className="rounded-lg border-2 border-[#F2B705] bg-[#151103] p-4 space-y-2">
                <div className="text-[#F2B705] text-xs uppercase tracking-widest font-bold">Confirm this extension</div>
                <p className="text-sm text-white/80">
                  {booking.name} keeps the {booking.vehicle} for <b className="text-white">+{days} day{days !== 1 ? "s" : ""}</b> — billed after the current paid-through period, no day counted twice.
                </p>
                <div className="text-sm space-y-1">
                  <Row label="Previous paid-through" value={fmtDate(curReturn)} />
                  <Row label="Extension start" value={`${fmtDate(curReturn)} (scheduled return time)`} />
                  <Row label="Days added" value={`+${days} day(s)${useWeekly && weeklyCheaper ? " · weekly block" : ""}`} />
                  <Row label="New paid-through / return" value={fmtDate(newReturn)} bold />
                  <div className="border-t border-white/10 my-1" />
                  <Row label="Base extension charge" value={money(baseCharge)} />
                  {(coverageAmount + waiverAmount + youngAmount + extraAmount) > 0 && (
                    <Row label="Continuing coverage & fees" value={money(coverageAmount + waiverAmount + youngAmount + extraAmount)} />
                  )}
                  {discountAmount > 0 && <Row label="Discount" value={`-${money(discountAmount)}`} tone="emerald" />}
                  <Row label="Total due" value={money(total)} bold tone="gold" />
                </div>
                <div className="flex gap-2 pt-1">
                  <button data-testid={`ext-confirm-create-${booking.id}`} disabled={busy}
                    onClick={async () => { await submit(false); setConfirming(false); }}
                    className="flex-1 bg-[#F2B705] text-black font-semibold text-sm px-4 py-2.5 rounded hover:bg-white transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                    {busy ? <Loader2 className="animate-spin" size={15} /> : <CheckCircle2 size={15} />} Approve Extension
                  </button>
                  <button data-testid={`ext-confirm-back-${booking.id}`} disabled={busy} onClick={() => setConfirming(false)}
                    className="border border-white/20 text-white/70 text-sm px-4 py-2.5 rounded hover:bg-white/10 transition-colors">
                    Go Back
                  </button>
                </div>
                <p className="text-white/40 text-[11px]">An invoice is created automatically and sent to the customer.</p>
              </div>
            )}
            <div className="flex items-center justify-center gap-2">
              <button data-testid="ext-copy-link" onClick={() => shareLink(false)}
                className="text-xs border border-white/15 text-white/60 hover:text-white px-3 py-1.5 rounded-md transition-colors">Copy customer extension link</button>
              <button data-testid="ext-sms-link" onClick={() => shareLink(true)}
                className="text-xs border border-[#F2B705]/40 text-[#F2B705] hover:bg-[#F2B705] hover:text-black px-3 py-1.5 rounded-md transition-colors">Text link to customer</button>
            </div>
            <p className="text-white/35 text-xs text-center">Creates a separate extension invoice — the original booking is never overwritten. If the original agreement is signed, the extension agreement is executed automatically (no re-signing) and emailed to the customer.</p>
          </div>

          {/* Extension / refund / undo history */}
          {audit.length > 0 && (
            <div data-testid={`extension-audit-${booking.id}`} className="border-t border-white/10 pt-3">
              <div className="text-xs uppercase tracking-widest text-white/40 mb-2">Extension History (audit)</div>
              <div className="space-y-1">
                {audit.map((a, i) => (
                  <div key={i} className="text-[11px] bg-[#0C0C0C] border border-white/5 rounded px-3 py-1.5 text-white/55">
                    <span className="text-white/35">{fmtDateTime(a.ts)}</span> · <span className="text-[#F2B705]/80">{a.action}</span> · {a.detail} <span className="text-white/30">— {a.by}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Rental Ledger */}
          {ledger.length > 0 && (
            <div data-testid={`rental-ledger-${booking.id}`} className="border-t border-white/10 pt-3">
              <div className="text-xs uppercase tracking-widest text-white/40 mb-2 flex items-center gap-1.5"><DollarSign size={12} /> Rental Ledger</div>
              <div className="space-y-1">
                {ledger.map((l, i) => (
                  <div key={i} className="flex items-center justify-between text-xs bg-[#0C0C0C] border border-white/5 rounded px-3 py-1.5">
                    <span className="text-white/60">{l.date} · {l.label}</span>
                    <span className={l.amount < 0 ? "text-emerald-400" : "text-white/80"}>{l.amount < 0 ? "-" : "+"}{money(Math.abs(l.amount))}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}

const Stat = ({ label, value, tone }) => (
  <div className="bg-[#0C0C0C] border border-white/10 rounded px-3 py-2">
    <div className="text-[10px] uppercase tracking-wide text-white/40">{label}</div>
    <div className={`text-sm font-semibold ${tone === "emerald" ? "text-emerald-400" : tone === "gold" ? "text-[#F2B705]" : "text-white/90"}`}>{value}</div>
  </div>
);

const Section = ({ n, title, children }) => (
  <div className="space-y-2">
    <div className="text-sm font-semibold text-white/80 flex items-center gap-2">
      <span className="h-5 w-5 rounded-full bg-white/10 text-white/60 text-[11px] flex items-center justify-center">{n}</span>{title}
    </div>
    <div className="pl-7">{children}</div>
  </div>
);

const Row = ({ label, value, tone, bold }) => (
  <div className="flex items-center justify-between">
    <span className="text-white/55">{label}</span>
    <span className={`${bold ? "font-semibold" : ""} ${tone === "emerald" ? "text-emerald-400" : tone === "gold" ? "text-[#F2B705]" : "text-white/85"}`}>{value}</span>
  </div>
);
